import type { Metadata } from "next";
import { SOCIAL_SHARE } from "../../social-share";

export const SITE_URL = SOCIAL_SHARE.siteUrl;
export const SITE_NAME = "doimpo";

/** Branded doimpo collab social share image (1200×630 JPEG). */
export const DEFAULT_OG_IMAGE = SOCIAL_SHARE.imageUrl;
export const DEFAULT_OG_IMAGE_ALT = SOCIAL_SHARE.imageAlt;

export const DEFAULT_TITLE = SOCIAL_SHARE.title;
export const DEFAULT_DESCRIPTION = SOCIAL_SHARE.description;

export type ResolvedPageMetadata = {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogImageAlt: string;
  ogType: string;
  twitterCard: string;
};

function absoluteUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function firstImageUrl(images: unknown): string | undefined {
  if (!images) return undefined;
  if (typeof images === "string") return absoluteUrl(images);
  if (!Array.isArray(images) || images.length === 0) return undefined;
  const first = images[0];
  if (typeof first === "string") return absoluteUrl(first);
  if (first && typeof first === "object" && "url" in first) {
    const url = (first as { url: string | URL }).url;
    return absoluteUrl(typeof url === "string" ? url : url.toString());
  }
  return undefined;
}

export function resolveMetadata(
  metadata: Metadata | undefined,
  pathname: string,
): ResolvedPageMetadata {
  const canonical =
    metadata?.alternates?.canonical ??
  `${SITE_URL}${pathname === "/" ? "" : pathname}`;

  const title = metadata?.title ?? DEFAULT_TITLE;
  const description = metadata?.description ?? DEFAULT_DESCRIPTION;
  const og = metadata?.openGraph ?? {};
  const twitter = metadata?.twitter ?? {};

  const ogTitle = (og.title as string | undefined) ?? title;
  const ogDescription = (og.description as string | undefined) ?? description;
  const ogImage =
    firstImageUrl(og.images) ??
    firstImageUrl(twitter.images) ??
    DEFAULT_OG_IMAGE;
  const ogImageAlt =
    (Array.isArray(og.images) &&
      og.images[0] &&
      typeof og.images[0] === "object" &&
      "alt" in og.images[0] &&
      typeof og.images[0].alt === "string" &&
      og.images[0].alt) ||
    DEFAULT_OG_IMAGE_ALT;
  const ogType = (og.type as string | undefined) ?? "website";
  const twitterCard = (twitter.card as string | undefined) ?? "summary_large_image";

  return {
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogImageAlt,
    ogType,
    twitterCard,
  };
}

export function withDefaultOgImage(metadata: Metadata): Metadata {
  const image = {
    url: DEFAULT_OG_IMAGE,
    width: SOCIAL_SHARE.imageWidth,
    height: SOCIAL_SHARE.imageHeight,
    alt: DEFAULT_OG_IMAGE_ALT,
    type: "image/jpeg",
  };

  return {
    ...metadata,
    openGraph: {
      ...metadata.openGraph,
      images: metadata.openGraph?.images ?? [image],
    },
    twitter: {
      card: "summary_large_image",
      ...metadata.twitter,
      images: metadata.twitter?.images ?? [DEFAULT_OG_IMAGE],
    },
  };
}

export const defaultSiteMetadata: Metadata = withDefaultOgImage({
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  openGraph: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    url: SITE_URL,
    siteName: SITE_NAME,
    type: "website",
  },
  twitter: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
});
